import { rankOf, suitOf } from '../core/cards.js';
import { FIVE_KINDS } from './types.js';
/**
 * Combo classification and comparison — the meat of Pusoy Dos. Pure functions
 * over card codes: no state, no turn order, no settlement. ./index.ts is the
 * only thing that knows whose turn it is.
 */
/** Low→high. The deuce is the top rank; the ace sits under it. */
export const PD_RANKS = [
    '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A', '2',
];
/** ♣ < ♠ < ♥ < ♦. Every rank tie between single cards falls through to this. */
export const PD_SUITS = { C: 0, S: 1, H: 2, D: 3 };
/** The card that must appear in the opening play, and whose holder leads. */
export const THREE_CLUBS = '3C';
/** The single nothing beats: top rank, top suit. */
export const TWO_OF_DIAMONDS = '2D';
/** Cards in a legal play, by type. Nothing else is a play — four of a kind
 *  alone is not, it has to arrive as quads+kicker inside a five. */
export const SIZE_OF = {
    single: 1,
    pair: 2,
    trip: 3,
    five: 5,
};
export const MAX_PLAY = 5;
export function rankIndex(code) {
    return PD_RANKS.indexOf(rankOf(code));
}
export function suitIndex(code) {
    return PD_SUITS[suitOf(code)];
}
/** Total order on single cards: rank first, then suit. 3♣ = 0, 2♦ = 51. */
export function cardValue(code) {
    return rankIndex(code) * 4 + suitIndex(code);
}
export const byCardValue = (a, b) => cardValue(a) - cardValue(b);
/**
 * Straight ladder, and the one place the deuce is NOT high.
 *
 * A straight is five distinct consecutive positions on 2,3,…,10,J,Q,K,A. So
 * the lowest straight is 2-3-4-5-6 and the highest is 10-J-Q-K-A; the wheel
 * A-2-3-4-5 and the wrap J-Q-K-A-2 are not straights at all. This is the
 * frozen production behaviour and the Dart client is aligned to it — do not
 * "fix" it into a wheel-friendly ladder without changing both.
 */
const LADDER = {
    A: 14, K: 13, Q: 12, J: 11, '10': 10,
    '9': 9, '8': 8, '7': 7, '6': 6, '5': 5, '4': 4, '3': 3, '2': 2,
};
export function straightPos(code) {
    return LADDER[rankOf(code)];
}
export function isStraight(group) {
    if (group.length !== 5)
        return false;
    const pos = group.map(straightPos);
    if (new Set(pos).size !== 5)
        return false;
    const sorted = [...pos].sort((a, b) => a - b);
    const lo = sorted[0] ?? 0;
    return sorted.every((p, i) => p === lo + i);
}
export function isFlush(group) {
    const first = group[0];
    if (first === undefined)
        return false;
    return group.every((c) => suitOf(c) === suitOf(first));
}
/** Highest card by the Pusoy Dos order — which decides straights and flushes. */
function topValue(group) {
    let top = -1;
    for (const c of group) {
        const v = cardValue(c);
        if (v > top)
            top = v;
    }
    return top;
}
/**
 * Classify a group of cards, or null if it is not a legal play.
 *
 * Note the asymmetry a straight carries: it is *detected* on the ace-high
 * ladder above but *ranked* by its highest card in Pusoy Dos order, where the
 * deuce is top. 2-3-4-5-6 is therefore the strongest straight in the game,
 * above 10-J-Q-K-A. That falls straight out of the two orders and is the
 * shipped behaviour; rules.test.ts pins it so nobody quietly changes it.
 */
export function classify(group) {
    const n = group.length;
    const first = group[0];
    if (first === undefined)
        return null;
    if (n === 1)
        return { type: 'single', kind: null, rank: cardValue(first) };
    if (n === 2) {
        const second = group[1];
        if (second === undefined || rankOf(first) !== rankOf(second))
            return null;
        return { type: 'pair', kind: null, rank: Math.max(cardValue(first), cardValue(second)) };
    }
    if (n === 3) {
        if (!group.every((c) => rankOf(c) === rankOf(first)))
            return null;
        // Trips compare on rank alone. The deck holds four of each rank, so two
        // trips of the same rank can never contest a trick and the suit tiebreak
        // that decides singles and pairs would never fire here.
        return { type: 'trip', kind: null, rank: rankIndex(first) };
    }
    if (n === 5)
        return classifyFive(group);
    return null;
}
function classifyFive(group) {
    const straight = isStraight(group);
    const flush = isFlush(group);
    if (straight && flush)
        return { type: 'five', kind: 'straightflush', rank: topValue(group) };
    const counts = new Map();
    for (const c of group) {
        const r = rankOf(c);
        counts.set(r, (counts.get(r) ?? 0) + 1);
    }
    // Quads and full houses rank on the four/three, never on the kicker or the
    // pair: the deck cannot produce two of either at the same rank.
    for (const [r, n] of counts)
        if (n === 4)
            return { type: 'five', kind: 'quads', rank: PD_RANKS.indexOf(r) };
    const trip = [...counts].find(([, n]) => n === 3);
    const pair = [...counts].find(([, n]) => n === 2);
    if (trip && pair)
        return { type: 'five', kind: 'fullhouse', rank: PD_RANKS.indexOf(trip[0]) };
    if (flush)
        return { type: 'five', kind: 'flush', rank: topValue(group) };
    if (straight)
        return { type: 'five', kind: 'straight', rank: topValue(group) };
    return null;
}
/** Position on the five-card ladder; -1 for anything that is not a five. */
export function fiveTier(c) {
    return c.kind === null ? -1 : FIVE_KINDS.indexOf(c.kind);
}
/**
 * Does `a` beat the standing combo `b`?
 *
 * Types never cross: a pair cannot take a single and a five cannot take a trip.
 * Within fives the category decides first, and only two hands of the same
 * category fall through to `rank`.
 */
export function beats(a, b) {
    if (a.type !== b.type)
        return false;
    if (a.type !== 'five')
        return a.rank > b.rank;
    const at = fiveTier(a);
    const bt = fiveTier(b);
    if (at !== bt)
        return at > bt;
    return a.rank > b.rank;
}
/** Ordering used by legalPlays: weakest first, and total, so the list is stable. */
const TYPE_ORDER = { single: 0, pair: 1, trip: 2, five: 3 };
function comparePlays(a, b) {
    const t = TYPE_ORDER[a.combo.type] - TYPE_ORDER[b.combo.type];
    if (t !== 0)
        return t;
    const k = fiveTier(a.combo) - fiveTier(b.combo);
    if (k !== 0)
        return k;
    if (a.combo.rank !== b.combo.rank)
        return a.combo.rank - b.combo.rank;
    // Same strength, different cards — quads with a choice of kicker, a full
    // house that can be built two ways. Break by card value so replaying a
    // vector reproduces the same list on every machine.
    for (let i = 0; i < a.cards.length && i < b.cards.length; i++) {
        const d = cardValue(a.cards[i] ?? '') - cardValue(b.cards[i] ?? '');
        if (d !== 0)
            return d;
    }
    return a.cards.length - b.cards.length;
}
/** Every k-subset of `hand`, in index order, so a sorted hand yields sorted groups. */
function* combinations(hand, k) {
    const n = hand.length;
    if (k <= 0 || k > n)
        return;
    const idx = Array.from({ length: k }, (_, i) => i);
    for (;;) {
        yield idx.map((i) => hand[i] ?? '');
        let i = k - 1;
        while (i >= 0 && (idx[i] ?? 0) === n - k + i)
            i--;
        if (i < 0)
            return;
        idx[i] = (idx[i] ?? 0) + 1;
        for (let j = i + 1; j < k; j++)
            idx[j] = (idx[j - 1] ?? 0) + 1;
    }
}
/**
 * Every legal play from `hand`, weakest first.
 *
 * Exhaustive, not a sample: a 13-card hand has at most 1,664 candidate groups
 * of the four legal sizes, which is cheap enough to enumerate honestly. The
 * production reference emitted a heuristic subset for its bot, which meant a
 * seat could hold a beat that the "legal actions" list never showed. apply()
 * validates from first principles either way, so the only thing that cost was
 * trust in the list.
 *
 * `table` null means the lead is open and any type may be led.
 */
export function legalPlays(hand, table, mustHoldThreeClubs) {
    const sizes = table === null ? [1, 2, 3, 5] : [SIZE_OF[table.type]];
    const out = [];
    for (const k of sizes) {
        for (const group of combinations(hand, k)) {
            if (mustHoldThreeClubs && !group.includes(THREE_CLUBS))
                continue;
            const combo = classify(group);
            if (!combo)
                continue;
            if (table && !beats(combo, table))
                continue;
            out.push({ cards: group, combo });
        }
    }
    return out.sort(comparePlays);
}
/**
 * Does `hand` hold every card of `group`, *counting repeats*?
 *
 * The multiset check is the point. A hand never holds a card twice, so a group
 * naming one twice must be refused — otherwise "3♦, 3♦" classifies as a pair
 * and `withoutCards` only ever spends the one card, minting a combo out of thin
 * air. `parseAction` rejects duplicates on the wire, but ownership is this
 * function's job and `apply()` is a public entry point: it must not depend on a
 * validator one layer up to stay sound.
 */
export function holdsAll(hand, group) {
    const pool = new Set(hand);
    for (const c of group) {
        if (!pool.has(c))
            return false;
        pool.delete(c);
    }
    return true;
}
export function withoutCards(hand, group) {
    const drop = new Set(group);
    return hand.filter((c) => !drop.has(c));
}
//# sourceMappingURL=rules.js.map